import React, { useState, useEffect } from "react";
import {
  Button,
  Dropdown,
  DropdownItemProps,
  Icon,
  Modal,
} from "semantic-ui-react";
import {
  AlertNotificationRule,
  AlertRule,
  EmailNotificationParams,
  NotificationChannelParameters,
  NotificationChannelType,
  SlackNotificationParams,
  SmsNotificationParams,
  WebhookNotificationParams,
  WhatsAppNotificationParams,
} from "../../../../util";
import {
  StyledInput,
  StyledLabel,
  Row,
} from "../AlertRules/CreateAlertRuleModal";
import styled from "styled-components";
import Toggle from "../../../common/Toggle";
import CreateSlackNotificationChannel from "./NotificationChannels/CreateSlackNotificationChannel";
import CreateEmailNotificationChannel from "./NotificationChannels/CreateEmailNotificationChannel";
import CreateWebhookNotificationChannel from "./NotificationChannels/CreateWebhookNotificationChannel";
import CreateSmsNotificationChannel from "./NotificationChannels/CreateSmsNotificationChannel";
import CreateWhatsAppNotificationChannel from "./NotificationChannels/CreateWhatsAppNotificationChannel";
import { beamtoast } from "../../../common/CustomToast";
import {
  fetchAlertNotificationRules,
  fetchTimezones,
  testAlertNotificationRule,
} from "../../../../BytebeamClient";
import useAsyncEffect from "../../common/useAsyncEffect";
import LoadingAnimation from "../../../common/Loader";
import { ThinDivider } from "../../Dashboards/Panel/util";
import { NotificationRuleOperationType } from "./AlertNotificationRules";
import { capitalizeFirstLetter } from "../../util";
import { MoreInfoPopUp } from "../../../common/ActionsUtils";

export interface SlackNotificationChannel {
  type: "slack";
  channelId: string;
  botToken: string;
}

export interface WebhookNotificationChannel {
  type: "webhook";
  url: string;
  headers: { [key: string]: string };
}

export interface EmailNotificationChannel {
  type: "email";
  emails: string[];
}

export interface SmsNotificationChannel {
  type: "sms";
  phoneNumbers: string[];
}

export interface WhatsAppNotificationChannel {
  type: "whatsapp";
  phoneNumbers: string[];
  activationTemplateId: string;
  deactivationTemplateId: string;
}

export type NotificationChannel =
  | SlackNotificationChannel
  | WebhookNotificationChannel
  | EmailNotificationChannel
  | SmsNotificationChannel
  | WhatsAppNotificationChannel;

export interface NotificationRule {
  name: string;
  alertRuleId: string;
  notificationChannel: NotificationChannel;
  interval_seconds: number;
  notifyOnDeactivation: boolean;
  timezone: string;
}

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

interface CreateNotificationRuleModalProps {
  readonly open: boolean;
  readonly onOpen: () => void;
  readonly onClose: () => void;
  readonly alertNotificationRule?: AlertNotificationRule;
  readonly title: string;
  readonly onSubmit: (notificationRule: AlertNotificationRule) => void;
  readonly alertRules: AlertRule[];
  readonly notificationModalStep: number;
  readonly operationType: "create" | "update";
  readonly existingNotificationRules: AlertNotificationRule[];
}

function toNotificationRule(anr: AlertNotificationRule): NotificationRule {
  if (anr.channel_type === "webhook") {
    const channelParameters =
      anr.channel_parameters as WebhookNotificationParams;

    return {
      name: anr.name,
      alertRuleId: anr.alert_rule_id,
      interval_seconds: anr.interval_seconds,
      notificationChannel: {
        type: "webhook",
        url: channelParameters.url,
        headers: channelParameters.headers,
      },
      notifyOnDeactivation: anr.notify_on_deactivation || false,
      timezone: anr.timezone,
    };
  }

  if (anr.channel_type === "sms") {
    const channelParameters = anr.channel_parameters as SmsNotificationParams;

    return {
      name: anr.name,
      alertRuleId: anr.alert_rule_id,
      interval_seconds: anr.interval_seconds,
      notificationChannel: {
        type: "sms",
        phoneNumbers: channelParameters.phone_numbers,
      },
      notifyOnDeactivation: anr.notify_on_deactivation || false,
      timezone: anr.timezone,
    };
  }

  if (anr.channel_type === "email") {
    const channelParameters = anr.channel_parameters as EmailNotificationParams;

    return {
      name: anr.name,
      alertRuleId: anr.alert_rule_id,
      interval_seconds: anr.interval_seconds,
      notificationChannel: {
        type: "email",
        emails: channelParameters.emails,
      },
      notifyOnDeactivation: anr.notify_on_deactivation || false,
      timezone: anr.timezone,
    };
  }

  if (anr.channel_type === "slack") {
    const channelParameters = anr.channel_parameters as SlackNotificationParams;

    return {
      name: anr.name,
      alertRuleId: anr.alert_rule_id,
      interval_seconds: anr.interval_seconds,
      notificationChannel: {
        type: "slack",
        botToken: channelParameters.bot_token,
        channelId: channelParameters.slack_channel,
      },
      notifyOnDeactivation: anr.notify_on_deactivation || false,
      timezone: anr.timezone,
    };
  }

  if (anr.channel_type === "whatsapp") {
    const channelParameters =
      anr.channel_parameters as WhatsAppNotificationParams;

    return {
      name: anr.name,
      alertRuleId: anr.alert_rule_id,
      interval_seconds: anr.interval_seconds,
      notificationChannel: {
        type: "whatsapp",
        phoneNumbers: channelParameters.phone_numbers,
        activationTemplateId: channelParameters.activation_template_id,
        deactivationTemplateId: channelParameters.deactivation_template_id,
      },
      notifyOnDeactivation: anr.notify_on_deactivation || false,
      timezone: anr.timezone,
    };
  }

  throw new Error("Unknown channel type");
}

export default function CreateNotificationRuleModal(
  props: CreateNotificationRuleModalProps
) {
  const {
    open,
    onOpen,
    onClose,
    title,
    onSubmit,
    alertRules,
    alertNotificationRule,
    operationType,
  } = props;

  const alertJson = `{
    "id": "alert rule id",
    "name": "alert rule name",
    "notification_rule_id": "notification rule id",
    "notification_rule_name": "notification rule name",
    "session_id": "alert session id",
    "start_time": "2025-05-17 16:11:54.000 IST",
    "end_time": "2025-05-17 23:59:59.000 IST",
    "device": {
        "id": 7,
        "metadata": {"city": "bengaluru"},
        "state": {"range": 53101, "timestamp": "2025-05-17T23:59:59.639"},
        "status": "active",
        "action": {
            "action_id": 15298,
            "type": "update_config",
            "progress": 100,
            "status": "Completed",
            "user_name": "Test User",
            "user_email": "test@bytebeam.io",
            "errors": "[]",
            "created_at": "2025-05-17T18:00:46Z",
            "updated_at": "2025-05-17T18:01:37Z"
    }},
    "activation_values": {"number_of_records": 100000},
    "deactivation_values": {}
}`;

  const defaultActivationTemplate = `
{% for alert in alerts -%}
    Alert {{ alert.name }} triggered at {{ alert.start_time }} on device {{ alert.device.id }}
From ByteBeam
    {% if not loop.last %}

    {% endif %}
{% endfor %}
`;

  const defaultDeactivationTemplate = `
{% for alert in alerts -%}
    Alert {{ alert.name }} resolved at {{ alert.end_time }} on device {{ alert.device.id }}
From ByteBeam
    {% if not loop.last %}

    {% endif %}
{% endfor %}
`;

  function getNotificationRuleSchema(
    channelType: NotificationChannelType
  ): NotificationRule {
    let rule: NotificationRule = {
      name: "",
      alertRuleId: "",
      notificationChannel: {
        type: "email",
        emails: [],
      },
      interval_seconds: 60,
      notifyOnDeactivation: false,
      timezone: "UTC",
    };
    switch (channelType) {
      case "slack":
        rule = {
          name: "",
          alertRuleId: "",
          notificationChannel: {
            type: "slack",
            channelId: "",
            botToken: "",
          },
          interval_seconds: 60,
          notifyOnDeactivation: false,
          timezone: "UTC",
        };
        break;
      case "email":
        rule = {
          name: "",
          alertRuleId: "",
          notificationChannel: {
            type: "email",
            emails: [],
          },
          interval_seconds: 60,
          notifyOnDeactivation: false,
          timezone: "UTC",
        };
        break;
      case "webhook":
        rule = {
          name: "",
          alertRuleId: "",
          notificationChannel: {
            type: "webhook",
            url: "",
            headers: { "": "" },
          },
          interval_seconds: 60,
          notifyOnDeactivation: false,
          timezone: "UTC",
        };

        break;
      case "sms":
        rule = {
          name: "",
          alertRuleId: "",
          notificationChannel: {
            type: "sms",
            phoneNumbers: [],
          },
          interval_seconds: 60,
          notifyOnDeactivation: false,
          timezone: "UTC",
        };

        break;
      case "whatsapp":
        rule = {
          name: "",
          alertRuleId: "",
          notificationChannel: {
            type: "whatsapp",
            phoneNumbers: [],
            activationTemplateId: "",
            deactivationTemplateId: "",
          },
          interval_seconds: 60,
          notifyOnDeactivation: false,
          timezone: "UTC",
        };
        break;
      default:
        rule = {
          name: "",
          alertRuleId: "",
          notificationChannel: {
            type: "email",
            emails: [],
          },
          interval_seconds: 60,
          notifyOnDeactivation: false,
          timezone: "UTC",
        };
    }

    return rule;
  }

  const notification = alertNotificationRule
    ? toNotificationRule(alertNotificationRule)
    : getNotificationRuleSchema("email");

  const [notificationRule, setNotificationRule] =
    useState<NotificationRule>(notification);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loadingTestNotification, setLoadingTestNotification] = useState(false);
  const [selectedNotificationType, setSelectedNotificationType] =
    useState<NotificationChannelType>("email");

  const [timezones, setTimezones] = useState<string[]>([]);

  const [activationTemplate, setActivationTemplate] = useState<
    string | undefined
  >(defaultActivationTemplate);

  const [deactivationTemplate, setDeactivationTemplate] = useState<
    string | undefined
  >(defaultDeactivationTemplate);

  const [notificationTemplateLoading, setNotificationTemplateLoading] =
    useState(false);
  let fetchNotificationRuleAbortController = new AbortController();

  const setIsValid = (isValid: boolean) => {
    setButtonDisabled(!isValid);
  };

  const createNotificationChannel = (channelType: NotificationChannelType) => {
    setNotificationRule(getNotificationRuleSchema(channelType));
  };

  const setNotificationChannel = (channel: NotificationChannel) => {
    if (notificationRule) {
      setNotificationRule({
        ...notificationRule,
        notificationChannel: channel,
      });
    }
  };

  const renderNotificationChannel = () => {
    return (
      <>
        {/* Render Notification Form based on selection */}
        {notificationRule.notificationChannel.type === "email" && (
          <CreateEmailNotificationChannel
            channel={notificationRule.notificationChannel}
            setChannel={setNotificationChannel}
            setIsValid={setIsValid}
            notificationRule={notificationRule}
          />
        )}
        {notificationRule.notificationChannel.type === "slack" && (
          <CreateSlackNotificationChannel
            channel={notificationRule.notificationChannel}
            setChannel={setNotificationChannel}
            setIsValid={setIsValid}
            notificationRule={notificationRule}
          />
        )}
        {notificationRule.notificationChannel.type === "webhook" && (
          <CreateWebhookNotificationChannel
            channel={notificationRule.notificationChannel}
            setChannel={setNotificationChannel}
            setIsValid={setIsValid}
            notificationRule={notificationRule}
          />
        )}
        {notificationRule.notificationChannel.type === "sms" && (
          <CreateSmsNotificationChannel
            channel={notificationRule.notificationChannel}
            setChannel={setNotificationChannel}
            setIsValid={setIsValid}
            notificationRule={notificationRule}
          />
        )}
        {notificationRule.notificationChannel.type === "whatsapp" && (
          <CreateWhatsAppNotificationChannel
            channel={notificationRule.notificationChannel}
            setChannel={setNotificationChannel}
            setIsValid={setIsValid}
            notificationRule={notificationRule}
          />
        )}
      </>
    );
  };

  const renderNotificationRuleName = () => {
    return (
      <StyledInput labelPosition="left">
        <StyledLabel>Notification Rule Name</StyledLabel>
        <input
          title="Notification Rule Name"
          placeholder={`${capitalizeFirstLetter(notificationRule?.notificationChannel.type ?? "")} Notification Rule`}
          value={notificationRule?.name}
          onChange={(e) => {
            if (notificationRule) {
              setNotificationRule({
                ...notificationRule,
                name: e.target.value,
              });
            }
          }}
        />
      </StyledInput>
    );
  };

  const renderAlertId = () => {
    const alertRuleIdOptions: DropdownItemProps[] = alertRules.map((rule) => {
      return {
        key: rule.id,
        value: rule.id,
        text: rule.name,
      };
    });

    return (
      <Row
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
        }}
      >
        {/* Alert Rule Dropdown */}
        <div style={{ flex: 1, minWidth: "200px" }}>
          <StyledInput labelPosition="left">
            <StyledLabel>Alert Rule</StyledLabel>
            <Dropdown
              fluid
              selection
              search
              placeholder="Alert Rule"
              options={alertRuleIdOptions}
              value={notificationRule?.alertRuleId}
              onChange={(_e, d) => {
                if (notificationRule) {
                  setNotificationRule({
                    ...notificationRule,
                    alertRuleId: d.value as string,
                  });
                }
              }}
              disabled={operationType === NotificationRuleOperationType.Update}
              style={{
                border: "none",
              }}
            />
          </StyledInput>
        </div>

        {/* Notify on Deactivation Toggle */}
        {renderNotifyOnDeactivation()}
      </Row>
    );
  };

  const renderAlertTimezone = () => {
    const timezoneOptions: DropdownItemProps[] = timezones.length
      ? timezones.map((timezone, key) => {
          return {
            key,
            value: timezone,
            text: timezone,
          };
        })
      : [];

    return (
      <StyledInput labelPosition="left">
        <StyledLabel>Timezone</StyledLabel>
        <Dropdown
          fluid
          selection
          search
          loading={timezones.length === 0}
          placeholder="Timezones"
          options={timezoneOptions}
          value={notificationRule?.timezone ?? "UTC"}
          onChange={(_e, d) => {
            if (notificationRule) {
              setNotificationRule({
                ...notificationRule,
                timezone: d.value as string,
              });
            }
          }}
          style={{
            border: "none",
          }}
        />
      </StyledInput>
    );
  };

  const renderInterval = () => {
    return (
      <StyledInput labelPosition="left">
        <StyledLabel>Interval (seconds)</StyledLabel>

        <input
          title="Interval (seconds)"
          type="number"
          value={notificationRule?.interval_seconds}
          onChange={(e) => {
            if (notificationRule) {
              setNotificationRule({
                ...notificationRule,
                interval_seconds: parseInt(e.target.value),
              });
            }
          }}
        />
      </StyledInput>
    );
  };

  const renderNotifyOnDeactivation = () => {
    return (
      <Row style={{ marginBottom: "15px" }}>
        <label style={{ marginRight: "10px" }}>Notify on Deactivation</label>
        <Toggle
          id="notifyOnDeactivationToggle"
          checked={notificationRule?.notifyOnDeactivation || false}
          onChange={() => {
            if (notificationRule) {
              setNotificationRule({
                ...notificationRule,
                notifyOnDeactivation: !notificationRule.notifyOnDeactivation,
              });
            }
          }}
          style={{ top: "2px", marginRight: "10px" }}
        />
      </Row>
    );
  };

  const renderTemplate = () => {
    if (notificationRule?.notificationChannel.type === "whatsapp") {
      return null; // Don't render template for WhatsApp
    }

    return (
      <Column>
        <Row style={{ marginTop: "12px" }}>
          <label>
            Customize Template{" "}
            <MoreInfoPopUp
              inverted
              trigger={<Icon name="question circle outline" />}
              content={
                <>
                  <h5
                    style={{
                      borderBottom: "1px solid #ccc",
                    }}
                  >
                    Sample Template Data:
                  </h5>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      lineHeight: "1",
                      fontSize: "13px",
                    }}
                  >
                    {alertJson}
                  </div>
                </>
              }
              position="top center"
              wide="very"
            />
          </label>

          <Toggle
            id="advancedToggle"
            checked={showAdvanced}
            onChange={() => setShowAdvanced(!showAdvanced)}
            style={{ top: "2px", marginLeft: "6px" }}
          />
        </Row>

        {showAdvanced && (
          <>
            {notificationTemplateLoading ? (
              <LoadingAnimation
                loadingText="Loading notification rule template..."
                loaderSize="30px"
                fontSize="14px"
                marginTopText="5px"
                loaderContainerHeight="100px"
                loaderContainerMargin="10px 0px 10px 0px"
              />
            ) : (
              <>
                {notificationRule?.notificationChannel.type === "sms" && (
                  <p style={{ marginBottom: "15px" }}>
                    The template should EXACTLY match the template registered
                    with Department of Telecommunications (DoT). One SMS will be
                    sent for each alert.
                  </p>
                )}

                <label> Alert Activation Template </label>
                <textarea
                  value={activationTemplate}
                  onChange={(e) => {
                    setActivationTemplate(e.target.value);
                  }}
                  rows={10}
                  style={{ width: "100%", marginBottom: "15px" }}
                />

                <label> Alert Deactivation Template </label>
                <textarea
                  value={deactivationTemplate}
                  onChange={(e) => {
                    setDeactivationTemplate(e.target.value);
                  }}
                  rows={10}
                  style={{ width: "100%", marginBottom: "15px" }}
                />
              </>
            )}
          </>
        )}
      </Column>
    );
  };

  const renderTestButton = () => {
    return (
      <Button
        primary
        disabled={buttonDisabled}
        loading={loadingTestNotification}
        onClick={handleTestNotificationRule}
      >
        Test Notification Rule
      </Button>
    );
  };

  const createNotificationRule = () => {
    if (notificationRule) {
      let channelParameters: NotificationChannelParameters;

      if (notificationRule.notificationChannel.type === "slack") {
        channelParameters = {
          slack_channel: notificationRule.notificationChannel.channelId,
          bot_token: notificationRule.notificationChannel.botToken,
        };
      } else if (notificationRule.notificationChannel.type === "email") {
        channelParameters = {
          emails: notificationRule.notificationChannel.emails.filter(
            (e) => e !== ""
          ),
        };
      } else if (notificationRule.notificationChannel.type === "webhook") {
        channelParameters = {
          url: notificationRule.notificationChannel.url,
          headers: notificationRule.notificationChannel.headers,
        };
      } else if (notificationRule.notificationChannel.type === "sms") {
        channelParameters = {
          phone_numbers:
            notificationRule.notificationChannel.phoneNumbers.filter(
              (e) => e !== ""
            ),
        };
      } else if (notificationRule.notificationChannel.type === "whatsapp") {
        channelParameters = {
          phone_numbers:
            notificationRule.notificationChannel.phoneNumbers.filter(
              (e) => e !== ""
            ),
          activation_template_id:
            notificationRule.notificationChannel.activationTemplateId,
          deactivation_template_id:
            notificationRule.notificationChannel.deactivationTemplateId,
        };
      } else {
        console.log("Unknown notification channel type");
        return;
      }

      const alertNotificationRule: AlertNotificationRule = {
        id: props.alertNotificationRule?.id,
        name: notificationRule.name,
        alert_rule_id: notificationRule.alertRuleId,
        interval_seconds: notificationRule.interval_seconds,
        channel_type: notificationRule.notificationChannel.type,
        channel_parameters: channelParameters,
        timezone: notificationRule.timezone,
        activation_template:
          notificationRule.notificationChannel.type === "whatsapp"
            ? undefined
            : activationTemplate,
        deactivation_template:
          notificationRule.notificationChannel.type === "whatsapp"
            ? undefined
            : deactivationTemplate,
        notify_on_deactivation: notificationRule.notifyOnDeactivation,
      };

      return alertNotificationRule;
    }
  };

  const handleSubmit = () => {
    const alertNotificationRule =
      createNotificationRule() as AlertNotificationRule;

    if (alertNotificationRule.name?.trim() === "") {
      beamtoast.error("Notification Rule Name cannot be empty");
      return;
    }
    // duplicate notification rule name validation
    if (
      props.existingNotificationRules &&
      props.existingNotificationRules.length > 0
    ) {
      if (
        props.existingNotificationRules.some(
          (rule) =>
            rule.name.toLowerCase() ===
              alertNotificationRule.name.trim().toLowerCase() &&
            rule.id !== props.alertNotificationRule?.id
        )
      ) {
        beamtoast.error("Notification rule name already exists");
        return;
      }
    }

    const isWhatsAppChannel = alertNotificationRule.channel_type === "whatsapp";
    const notifyOnDeactivation = alertNotificationRule.notify_on_deactivation;

    // Validate non-WhatsApp notification rules
    if (!isWhatsAppChannel) {
      if (!alertNotificationRule.activation_template?.trim()) {
        beamtoast.error("Activation template cannot be empty");
        return;
      }

      if (
        notifyOnDeactivation &&
        !alertNotificationRule.deactivation_template?.trim()
      ) {
        beamtoast.error("Deactivation template cannot be empty");
        return;
      }
    }

    // Validate WhatsApp notification rules
    if (isWhatsAppChannel) {
      const whatsappParams =
        alertNotificationRule.channel_parameters as WhatsAppNotificationParams;

      if (!whatsappParams?.activation_template_id) {
        beamtoast.error("Activation template ID cannot be empty");
        return;
      }

      if (notifyOnDeactivation && !whatsappParams?.deactivation_template_id) {
        beamtoast.error("Deactivation template ID cannot be empty");
        return;
      }
    }

    onSubmit(alertNotificationRule);
    handleModalClose();
  };

  const handleTestNotificationRule = async () => {
    if (notificationRule) {
      const alertNotificationRule =
        createNotificationRule() as AlertNotificationRule;
      setLoadingTestNotification(true);
      try {
        await testAlertNotificationRule(alertNotificationRule);
        beamtoast.success(`Sent test notification`);
      } catch (e) {
        console.error("Error testing alert notification rule:", e);
        beamtoast.error(`Failed to send test notification`);
      } finally {
        setLoadingTestNotification(false);
      }
    }
  };

  const handleModalClose = () => {
    setNotificationRule(
      getNotificationRuleSchema(selectedNotificationType || "email")
    );
    setButtonDisabled(true);
    setShowAdvanced(false);
    setActivationTemplate(defaultActivationTemplate);
    setDeactivationTemplate(defaultDeactivationTemplate);
    onClose();
  };

  const fetchAllTimezones = async () => {
    const res = await fetchTimezones();
    setTimezones(res);
  };

  // Fetch notification rule and set the template, if failed set the value to default template.
  const fetchAlertNotificationRuleTemplate = async () => {
    if (alertNotificationRule) {
      try {
        fetchNotificationRuleAbortController.abort();
        fetchNotificationRuleAbortController = new AbortController();
        setNotificationTemplateLoading(true);
        const res = await fetchAlertNotificationRules(
          alertNotificationRule,
          fetchNotificationRuleAbortController.signal
        );
        setActivationTemplate(
          res?.activation_template || defaultActivationTemplate
        );
        setDeactivationTemplate(
          res?.deactivation_template || defaultDeactivationTemplate
        );
      } catch (e) {
        console.error("Error fetching alert notification rule template:", e);
      } finally {
        setNotificationTemplateLoading(false);
      }
    } else {
      setActivationTemplate(defaultActivationTemplate);
      setDeactivationTemplate(defaultDeactivationTemplate);
    }
  };

  useAsyncEffect(fetchAlertNotificationRuleTemplate, [alertNotificationRule]);
  // Fetch notification rule and set the template, if failed set the value to default template.
  useAsyncEffect(fetchAllTimezones, []);

  useEffect(() => {
    const notification = alertNotificationRule
      ? toNotificationRule(alertNotificationRule)
      : getNotificationRuleSchema("email");

    setNotificationRule(notification);
    setSelectedNotificationType(notification.notificationChannel.type);

    // Abort controller cleanup, aborting fetch requests when component unmounts.
    return () => {
      fetchNotificationRuleAbortController.abort();
    };
  }, [alertNotificationRule, operationType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      className="dark"
      onClose={() => handleModalClose()}
      onOpen={onOpen}
      open={open}
      size={"tiny"}
    >
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content>
        {/* Notification Channel Selection Dropdown */}
        <StyledInput labelPosition="left">
          <StyledLabel>Choose Notification Channel</StyledLabel>
          <Dropdown
            fluid
            selection
            search
            placeholder="Choose Notification Channel"
            options={[
              { key: "slack", text: "Slack", value: "slack" },
              { key: "email", text: "Email", value: "email" },
              { key: "webhook", text: "Webhook", value: "webhook" },
              { key: "whatsapp", text: "WhatsApp", value: "whatsapp" },
              { key: "sms", text: "SMS (Deprecated Soon)", value: "sms", disabled: true},
            ]}
            value={selectedNotificationType}
            onChange={(_e, d) => {
              setSelectedNotificationType(d.value as NotificationChannelType);
              createNotificationChannel(d.value as NotificationChannelType);
            }}
            style={{ border: "none" }}
            disabled={operationType === NotificationRuleOperationType.Update}
          />
        </StyledInput>

        <ThinDivider />

        <div>{notificationRule && renderNotificationRuleName()}</div>

        {notificationRule && renderAlertId()}

        <div>{renderNotificationChannel()}</div>

        {notificationRule && (
          <>
            {renderInterval()}
            {renderAlertTimezone()}
            {notificationRule.notificationChannel.type === "slack" && (
              <p>
                <b>Note: </b>Please ensure that the bot is added to the channel
                for notifications to work correctly.
              </p>
            )}
            {renderTemplate()}
          </>
        )}
      </Modal.Content>
      <Modal.Actions
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        {renderTestButton()}

        <Row style={{ display: "flex", gap: "10px" }}>
          <Button secondary onClick={handleModalClose}>
            Cancel
          </Button>

          <Button
            type="submit"
            primary
            disabled={
              buttonDisabled ||
              (notificationRule.interval_seconds
                ? notificationRule.interval_seconds <= 0
                : true)
            }
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Row>
      </Modal.Actions>
    </Modal>
  );
}
